import { IconText } from './AppIcon';
import React, { ButtonHTMLAttributes, ReactNode, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
  children: ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  style,
  ...props
}) => {
  const { isDark } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const sizeStyles = {
    sm: { padding: '6px 12px', fontSize: '14px', gap: '6px' },
    md: { padding: '10px 16px', fontSize: '16px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '18px', gap: '8px' },
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: isHovered ? '#1D4ED8' : '#2563EB',
          color: '#FFFFFF',
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: isHovered 
            ? (isDark ? '#4B5563' : '#E5E7EB') 
            : (isDark ? '#374151' : '#F3F4F6'),
          color: isDark ? '#D1D5DB' : '#374151',
          border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
        };
      case 'danger':
        return {
          backgroundColor: isHovered ? '#B91C1C' : '#DC2626',
          color: '#FFFFFF',
          border: 'none',
        };
      case 'ghost':
        return {
          backgroundColor: isHovered 
            ? (isDark ? '#374151' : '#F3F4F6') 
            : 'transparent',
          color: isDark ? '#D1D5DB' : '#374151',
          border: 'none',
        };
      default:
        return {};
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <button
      className={`ui-button ${className}`}
      disabled={disabled || loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '44px',
        fontWeight: 600,
        borderRadius: '8px',
        transition: 'all 0.2s',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.5 : 1,
        ...sizeStyles[size],
        ...variantStyles,
        ...style,
      }}
      {...props}
    >
      {loading ? (
        <svg 
          style={{ 
            width: '20px', 
            height: '20px', 
            animation: 'spin 1s linear infinite' 
          }} 
          viewBox="0 0 24 24"
        >
          <circle 
            style={{ opacity: 0.25 }} 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4" 
            fill="none" 
          />
          <path 
            style={{ opacity: 0.75 }} 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
          />
        </svg>
      ) : icon}
      <span>{typeof children === 'string' ? <IconText text={children} /> : children}</span>
    </button>
  );
};

export default Button;
